import { pick } from 'lodash-es'
import { BufferGeometry, CanvasTexture, Object3D, Points, PointsMaterial, Vector3 } from 'three'
import type { Line2 } from 'three/examples/jsm/lines/Line2.js'
import type { LineSegments2 } from 'three/examples/jsm/lines/LineSegments2.js'
import type { Text } from 'troika-three-text'
import { disposeCadText, scheduleCadTextSync } from '@/plugins/cad/diagnostics/cadTextSync'
import {
  type AngularDimensionEntity,
  getAngularDimensionData,
  getAngularDimensionDefinitionPoints,
  getAngularDimensionLegLines,
} from '@/plugins/cad/module/CADModule/model/entity'
import {
  createCadLine2Object,
  createCadLineSegments2Object,
} from '@/plugins/cad/module/CADModule/object/material/CadLineFactory'
import type { LineTypeMaterial } from '@/plugins/cad/module/CADModule/object/material/MaterialManager'
import { MaterialUtils } from '@/plugins/cad/module/CADModule/object/material/MaterialUtils'
import { createCadText } from '@/plugins/cad/module/CADModule/object/text/CadTextFactory'
import { DrawUtils } from '@/plugins/cad/module/utils'
import { beginDisposeOnce } from '@/shared/utils/disposeOnce'

const ENDPOINT_POINT_SIZE = 4

/** 角度标注渲染对象 */
export class AngularDimensionObject extends Object3D {
  private legLineObject: LineSegments2
  private arcObject: Line2
  private endpointObject: Points
  private textObject: Text
  private readonly endpointPointTexture = createEndpointPointTexture()
  /** 是否使用缓存材质，true 时销毁对象不释放材质 */
  public isCacheMaterial: boolean

  constructor(
    public entity: AngularDimensionEntity,
    material?: LineTypeMaterial,
    color?: number
  ) {
    super()
    this.isCacheMaterial = !!material
    const dimensionData = this.getDimensionData()
    const lineMaterial = material || this.createMaterial(color)

    this.legLineObject = this.createLegLineObject(dimensionData, lineMaterial)
    this.arcObject = this.createArcObject(dimensionData, lineMaterial)
    this.endpointObject = this.createEndpointObject(dimensionData, color)
    this.textObject = this.createTextObject(dimensionData, color)

    this.init()
  }

  private get materialColor() {
    return this.entity.color
  }

  /** 当前标注使用的线型材质 */
  get material() {
    return this.arcObject.material as LineTypeMaterial
  }

  private init() {
    this.add(this.legLineObject)
    this.add(this.arcObject)
    this.add(this.endpointObject)
    this.add(this.textObject)

    this.legLineObject.userData.entity = this.entity
    this.arcObject.userData.entity = this.entity
    this.endpointObject.userData.entity = this.entity
    this.textObject.userData.entity = this.entity
  }

  /** 更新几何体 */
  onUpdateGeometry() {
    const dimensionData = this.getDimensionData()

    this.legLineObject.geometry.dispose()
    this.legLineObject.geometry = DrawUtils.getLineSegmentsGeometryByLines(
      this.getLegLines(dimensionData)
    )
    this.legLineObject.geometry.computeBoundingBox()
    this.legLineObject.geometry.computeBoundingSphere()
    this.legLineObject.computeLineDistances()

    this.arcObject.geometry.dispose()
    this.arcObject.geometry = DrawUtils.getLineGeometryByPoints(
      DrawUtils.getArcPoints({
        center: { x: 0, y: 0 },
        radius: dimensionData.radius,
        startAngle: dimensionData.startAngle,
        endAngle: dimensionData.endAngle,
        clockwise: dimensionData.clockwise,
      })
    )
    this.arcObject.position.copy(dimensionData.intersection)
    this.arcObject.geometry.computeBoundingBox()
    this.arcObject.geometry.computeBoundingSphere()
    this.arcObject.computeLineDistances()

    this.updateEndpointObject(dimensionData)

    this.textObject.text = this.getDisplayText(dimensionData.angleDegrees)
    this.textObject.position.copy(dimensionData.textPosition)
    scheduleCadTextSync(this.textObject)
  }

  /**
   * 更新材质。
   *
   * @param material - 可选的外部缓存材质；未提供时根据实体样式新建
   * @param color - 可选颜色覆盖值，未提供时沿用实体自身颜色
   */
  onUpdateMaterial(material?: LineTypeMaterial, color?: number) {
    if (!this.isCacheMaterial) {
      ;(this.arcObject.material as LineTypeMaterial).dispose()
    }

    const lineMaterial = material || this.createMaterial(color)
    const materialColor = color ?? this.materialColor
    this.isCacheMaterial = !!material
    this.legLineObject.material = lineMaterial
    this.arcObject.material = lineMaterial

    ;(this.endpointObject.material as PointsMaterial).dispose()
    this.endpointObject.material = this.createEndpointMaterial(materialColor)

    this.textObject.color = `#${materialColor.toString(16).padStart(6, '0')}`
    this.textObject.fontSize = this.entity.textHeight
    scheduleCadTextSync(this.textObject)
  }

  /** 销毁 */
  dispose() {
    if (!beginDisposeOnce(this)) return

    // Three r186 起 Object3D 才提供 dispose()，仅用于派发 dispose 事件；旧 peer 无该方法，必须可选调用。
    super.dispose?.()
    this.legLineObject.geometry.dispose()
    this.arcObject.geometry.dispose()
    if (!this.isCacheMaterial) {
      ;(this.arcObject.material as LineTypeMaterial).dispose()
    }
    this.endpointObject.geometry.dispose()
    ;(this.endpointObject.material as PointsMaterial).dispose()
    this.endpointPointTexture?.dispose()
    disposeCadText(this.textObject)
  }

  /**
   * 克隆当前对象。
   *
   * @returns 基于同一实体新建的角度标注对象
   */
  clone() {
    return new AngularDimensionObject(this.entity) as any
  }

  private getDimensionData() {
    const { line1, line2, arcPoint, textPosition } = this.entity
    const dimensionData = getAngularDimensionData(line1, line2, arcPoint, textPosition)
    if (!dimensionData) {
      throw new Error('Invalid angular dimension geometry.')
    }
    return dimensionData
  }

  private createMaterial(color?: number): LineTypeMaterial {
    const materialOption = MaterialUtils.getMaterialOptionByEntity(this.entity)
    if (color !== undefined) {
      materialOption.color = color
    }
    return MaterialUtils.createMaterialByLineType(this.entity.lineType, materialOption)
  }

  private createLegLineObject(
    dimensionData: ReturnType<AngularDimensionObject['getDimensionData']>,
    material: LineTypeMaterial
  ) {
    const geometry = DrawUtils.getLineSegmentsGeometryByLines(this.getLegLines(dimensionData))
    const object = createCadLineSegments2Object(geometry, material)
    object.computeLineDistances()
    geometry.computeBoundingBox()
    geometry.computeBoundingSphere()
    return object
  }

  private createArcObject(
    dimensionData: ReturnType<AngularDimensionObject['getDimensionData']>,
    material: LineTypeMaterial
  ) {
    const geometry = DrawUtils.getLineGeometryByPoints(
      DrawUtils.getArcPoints({
        center: { x: 0, y: 0 },
        radius: dimensionData.radius,
        startAngle: dimensionData.startAngle,
        endAngle: dimensionData.endAngle,
        clockwise: dimensionData.clockwise,
      })
    )
    const object = createCadLine2Object(geometry, material)
    object.position.copy(dimensionData.intersection)
    object.computeLineDistances()
    geometry.computeBoundingBox()
    geometry.computeBoundingSphere()
    return object
  }

  private getLegLines(dimensionData: ReturnType<AngularDimensionObject['getDimensionData']>) {
    return getAngularDimensionLegLines(this.entity, dimensionData)
  }

  private createEndpointObject(
    dimensionData: ReturnType<AngularDimensionObject['getDimensionData']>,
    color?: number
  ) {
    const geometry = new BufferGeometry().setFromPoints(this.getEndpointPoints(dimensionData))
    geometry.computeBoundingBox()
    geometry.computeBoundingSphere()
    return new Points(geometry, this.createEndpointMaterial(color ?? this.materialColor))
  }

  private updateEndpointObject(
    dimensionData: ReturnType<AngularDimensionObject['getDimensionData']>,
    endpointObject = this.endpointObject
  ) {
    endpointObject.geometry.setFromPoints(this.getEndpointPoints(dimensionData))
    endpointObject.geometry.attributes.position.needsUpdate = true
    endpointObject.geometry.computeBoundingBox()
    endpointObject.geometry.computeBoundingSphere()
  }

  private createEndpointMaterial(color: number) {
    const materialOption = MaterialUtils.getMaterialOptionByEntity(this.entity)
    return new PointsMaterial({
      color,
      size: ENDPOINT_POINT_SIZE,
      sizeAttenuation: false,
      transparent: true,
      alphaTest: 0.5,
      map: this.endpointPointTexture,
      ...pick(materialOption, ['depthTest', 'depthWrite']),
    })
  }

  private getEndpointPoints(dimensionData: ReturnType<AngularDimensionObject['getDimensionData']>) {
    const { firstPoint, secondPoint } = getAngularDimensionDefinitionPoints(
      this.entity,
      dimensionData
    )
    return [
      dimensionData.intersection,
      new Vector3(firstPoint.x, firstPoint.y, 0),
      new Vector3(secondPoint.x, secondPoint.y, 0),
    ]
  }

  private createTextObject(
    dimensionData: ReturnType<AngularDimensionObject['getDimensionData']>,
    materialColor?: number
  ) {
    const color = materialColor ?? this.materialColor
    const object = createCadText()
    object.text = this.getDisplayText(dimensionData.angleDegrees)
    object.fontSize = this.entity.textHeight
    object.color = `#${color.toString(16).padStart(6, '0')}`
    object.position.copy(dimensionData.textPosition)
    object.anchorX = 'center'
    object.anchorY = 'bottom'

    const materialOption = MaterialUtils.getMaterialOptionByEntity(this.entity)
    object.material.depthTest = materialOption.depthTest ?? true
    object.material.depthWrite = materialOption.depthWrite ?? true
    scheduleCadTextSync(object)
    return object
  }

  private getDisplayText(angleDegrees: number) {
    return this.entity.dimensionText || `${angleDegrees.toFixed(2)}°`
  }
}

function createEndpointPointTexture(): CanvasTexture | undefined {
  if (typeof document === 'undefined') {
    return undefined
  }

  const size = 32
  const radius = size / 2 - 1
  const canvas = document.createElement('canvas')
  canvas.width = size
  canvas.height = size

  const context = canvas.getContext('2d')
  if (!context) {
    return undefined
  }

  context.clearRect(0, 0, size, size)
  context.fillStyle = '#ffffff'
  context.beginPath()
  context.arc(size / 2, size / 2, radius, 0, Math.PI * 2)
  context.fill()

  return new CanvasTexture(canvas)
}
